import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Card,
    CardContent,
    Grid,
    Chip,
    Alert,
    CircularProgress,
    Tooltip
} from '@mui/material';
import {
    Sync as SyncIcon,
    Preview as PreviewIcon,
    Add as AddIcon,
    Dashboard as StatsIcon,
    HealthAndSafety as HealthNetIcon
} from '@mui/icons-material';
import PrescriptionList from '../components/prescriptions/PrescriptionList';
import RecupFiltersDialog from '../components/prescriptions/RecupFiltersDialog';
import PrescriptionFilters from '../components/prescriptions/PrescriptionFilters';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import prescriptionService, { 
    PrescriptionFilter, 
    SyncStatsResponse,
    DeleteOptions,
    DeleteResult,
    Prescription
} from '../services/prescriptionService';
import './PrescriptionsPage.css';

const PrescriptionsPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [syncStats, setSyncStats] = useState<SyncStatsResponse['data'] | null>(null);
    const [filters, setFilters] = useState<PrescriptionFilter>({
        page: 1,
        limit: 50
    });

    // Dialog states
    const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedPrescription, setSelectedPrescription] = useState<Prescription | null>(null);
    const [searchResults, setSearchResults] = useState<Prescription[]>([]);
    
    // Mock user admin check - this should be replaced with actual auth logic
    const userIsAdmin = true; // TODO: Replace with actual admin check

    // Load sync statistics on component mount
    useEffect(() => {
        loadSyncStats();
    }, []);

    const loadSyncStats = async () => {
        try {
            const response = await prescriptionService.getSyncStats();
            setSyncStats(response.data);
        } catch (error: any) {
            console.error('Error loading sync stats:', error);
        }
    };

    const handleFiltersChange = (newFilters: PrescriptionFilter) => {
        setFilters({
            ...newFilters,
            page: 1 // Reset to first page when filters change
        });
        setError(null);
    };

    const handleSearchResults = (results: Prescription[]) => {
        setSearchResults(results);
    };

    const handleDeleteClick = (prescription: Prescription) => {
        setSelectedPrescription(prescription);
        setShowDeleteDialog(true);
    };

    const handleDeleteConfirm = async (options: DeleteOptions) => {
        if (!selectedPrescription) return;

        setLoading(true);
        setError(null);
        
        try {
            const result: DeleteResult = await prescriptionService.deletePrescription(selectedPrescription.id, options);
            
            // Messaggio di successo dettagliato
            if (options.isHardDelete && result.details) {
                const { local_deleted, recup_deleted, patient_fiscal_code } = result.details;
                let successMessage = result.message;
                
                if (patient_fiscal_code) {
                    successMessage += local_deleted && recup_deleted
                        ? ' (eliminata da database locale e RECUP)'
                        : local_deleted && !recup_deleted
                        ? ' (eliminata da database locale, errore RECUP)'
                        : ' (stato parziale)';
                }
                
                alert(successMessage); // Using alert for now - could be improved with proper notification system
            } else {
                alert(result.message || 'Prescrizione eliminata con successo');
            }
            
            setShowDeleteDialog(false);
            setSelectedPrescription(null);
            
            // Refresh sync stats after deletion
            await loadSyncStats();
            
        } catch (err: any) {
            console.error('Errore nell\'eliminazione della prescrizione:', err);
            
            let errorMessage = 'Errore nell\'eliminazione della prescrizione';
            if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenFiltersDialog = () => {
        setFiltersDialogOpen(true);
    };

    const handleCloseFiltersDialog = () => {
        setFiltersDialogOpen(false);
    };

    const handleSyncComplete = async () => {
        // Reload sync stats and refresh data
        await loadSyncStats();
        // Trigger a refresh of the prescription list if needed
        // This could be done through a callback prop to PrescriptionList
    };

    return (
        <Container maxWidth="xl" className="prescriptions-page">
            <Box sx={{ py: 3 }}>
                {/* Header */}
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <Typography variant="h4" component="h1" gutterBottom>
                            Gestione Prescrizioni
                        </Typography>
                        <Typography variant="subtitle1" color="textSecondary">
                            Sincronizzazione e gestione delle prescrizioni dal sistema RECUP
                        </Typography>
                    </div>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Configurazione filtri sincronizzazione ReCup">
                            <Button
                                variant="contained"
                                startIcon={<SyncIcon />}
                                onClick={handleOpenFiltersDialog}
                                color="primary"
                            >
                                Sync filtri ReCup
                            </Button>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Statistics Cards */}
                {syncStats && (
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <StatsIcon color="primary" sx={{ mr: 1 }} />
                                        <Typography variant="h6">
                                            Totale
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" color="primary">
                                        {syncStats.total}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Prescrizioni totali
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <SyncIcon color="success" sx={{ mr: 1 }} />
                                        <Typography variant="h6">
                                            Sincronizzate
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" color="success.main">
                                        {syncStats.byStatus?.synced?.count || 0}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Completate con successo
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <CircularProgress size={24} sx={{ mr: 1 }} />
                                        <Typography variant="h6">
                                            In Attesa
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" color="warning.main">
                                        {syncStats.byStatus?.pending?.count || 0}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Da sincronizzare
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <HealthNetIcon color="info" sx={{ mr: 1 }} />
                                        <Typography variant="h6">
                                            HealthNET
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" color="info.main">
                                        {Object.values(syncStats.byStatus || {}).reduce(
                                            (sum, status) => sum + (status.healthnetSynced || 0), 0
                                        )}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Sincronizzate con HealthNET
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* Search and Sync Controls */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" component="h2">
                                Ricerca Prescrizioni
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    sx={{ minWidth: 120 }}
                                >
                                    Cerca
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<SyncIcon />}
                                    color="success"
                                    disabled={searchResults.length === 0}
                                    sx={{ minWidth: 140 }}
                                >
                                    Sincronizza
                                </Button>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                {/* Prescription List */}
                <PrescriptionList
                    filters={filters}
                    onFiltersChange={setFilters}
                    onSyncComplete={handleSyncComplete}
                    onDelete={handleDeleteClick}
                    onSearchResults={handleSearchResults}
                />


                {/* Filters Dialog */}
                <RecupFiltersDialog
                    open={filtersDialogOpen}
                    onClose={handleCloseFiltersDialog}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    loading={loading}
                />

                {/* Delete Confirmation Dialog */}
                <DeleteConfirmationDialog
                    open={showDeleteDialog}
                    onClose={() => setShowDeleteDialog(false)}
                    onConfirm={handleDeleteConfirm}
                    title="Elimina Prescrizione"
                    itemName={selectedPrescription?.nre || ''}
                    entityType="prescrizione"
                    allowHardDelete={true}
                    userIsAdmin={userIsAdmin}
                    relatedData={{
                        patientFiscalCode: selectedPrescription?.patient_fiscal_code,
                        prescriptionDate: selectedPrescription?.prescription_date,
                        syncStatus: selectedPrescription?.sync_status
                    }}
                />
            </Box>
        </Container>
    );
};

export default PrescriptionsPage;